// The Rails API returns errors in several shapes (and api.ts camelCases keys):
//
//   { errors: ["Invalid login credentials. Please try again."] }
//   { errors: { fullMessages: ["Email has already been taken"] } }
//   { errors: { locked: ["..."], fullMessages: ["..."] } }
//   { error: "Something went wrong" }
interface ApiErrorShape {
  status?: number;
  error?: string;
  errors?:
    | string
    | string[]
    | {
        fullMessages?: string[];
        email?: string[];
        locked?: string[];
      };
}

// Extracts a human-readable error message from a thrown API response.
// The destructuring in any single call site tends to miss at least one shape
// above, hence this shared helper.
export const extractApiErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object") {
    return typeof error === "string" ? error : undefined;
  }

  const e = error as ApiErrorShape;

  if (Array.isArray(e.errors)) {
    return typeof e.errors[0] === "string" ? e.errors[0] : undefined;
  }

  if (e.errors && typeof e.errors === "object") {
    if (e.errors.fullMessages?.[0]) return e.errors.fullMessages[0];
    if (e.errors.email?.[0]) {
      const emailError = e.errors.email[0];
      return emailError.toLowerCase().includes("email")
        ? emailError
        : `Email ${emailError}`;
    }
    return e.errors.locked?.[0] || undefined;
  }

  if (typeof e.errors === "string") return e.errors;
  if (typeof e.error === "string") return e.error;

  return undefined;
};

// True when a thrown API error represents an authentication failure that should
// force a logout. Prefer the HTTP status stamped by callApi; fall back to the
// legacy message strings the API used before the status was attached. Matching
// on status (not message text) is what stops a 401 from being silently
// swallowed — the API's "Not authenticated" body has no field this could key
// off otherwise.
export const isUnauthorizedError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;

  const e = error as ApiErrorShape;
  if (e.status === 401) return true;

  return [
    "You need to sign in or sign up before continuing.",
    "No payload or user id",
  ].includes(e.error ?? "");
};
